import React, { useState, useRef } from 'react';
import DropdownMenu from '../ui/DropdownMenu';
import Badge from '../ui/Badge';
import MediumButton from '../ui/MediumButton';
import ToggleButton from '../ui/ToggleButton';
import AnimatedCredits from '../ui/AnimatedCredits';
import ModeSwitch from '../ui/ModeSwitch';
import colors from '../../utils/colors';

import {
  MusicNoteIcon,
  TriangleDownIcon,
  ChevronDownIcon,
  SidebarRightIcon
} from '../../utils/icons';

const RotatableChevron = ({ isRotated }) => (
  <div className={`w-4 h-4 flex items-center justify-center transform transition-transform duration-200 ${isRotated ? 'rotate-180' : ''}`}>
    <ChevronDownIcon />
  </div>
);


const Header = ({ count = "5,000", selectedMode = "Simple", onModeChange, version = "v4.5", onVersionChange, isRemixMode = false, workspaceVisible = true, onWorkspaceToggle }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const versionButtonRef = useRef(null);

  // Model options for the dropdown
  const modelOptions = [
    {
      value: "v4.5",
      label: (
        <div className="flex items-center gap-1">
          <span>v4.5</span>
          <Badge text="Beta" variant="secondary" size="small" />
          <Badge text="Pro" variant="tertiary" size="small" />
        </div>
      ),
      description: "Smarter prompts, better genre mashups, faster generations, max 8 min"
    },
    {
      value: "v4",
      label: (
        <div className="flex items-center gap-1">
          <span>v4</span>
          <Badge text="Pro" variant="tertiary" size="small" />
        </div>
      ),
      description: "Improved vocal quality\nmax 4 min"
    },
    {
      value: "v3.5",
      label: "v3.5",
      description: "Better song structure\nmax 4 min"
    },
    {
      value: "v3",
      label: "v3",
      description: "Older\nmax 2 min"
    },
    {
      value: "v2",
      label: "v2",
      description: "Vintage\nmax 2 min"
    }
  ];

  const handleVersionClick = () => {
    setShowDropdown(!showDropdown);
  };

  const handleVersionSelect = (newVersion) => {
    onVersionChange?.(newVersion);
    setShowDropdown(false);
  };

  return (
    <div className="flex justify-between items-center w-full p-4 border-b border-white/10 relative" style={{ backgroundColor: colors.bg.primary }}>
      {/* Left side - Credits and Version */}
      <div className="flex items-center gap-2" style={{ width: '160px' }}>
        {/* Credits Counter */}
        <MediumButton
          variant="tertiary"
          leftIcon={<MusicNoteIcon />}
          text={<AnimatedCredits credits={count} className="text-xs" />}
          className="w-[76px]"
        />

        {/* Version Dropdown - Visible in both Chat and Custom modes */}
        {!isRemixMode && (
          <div ref={versionButtonRef} className="relative">
            <MediumButton
              variant="tertiary"
              text={version}
              onClick={handleVersionClick}
              rightIcon={<TriangleDownIcon />}
            />

            {/* Dropdown Menu */}
            <DropdownMenu
              isOpen={showDropdown}
              onClose={() => setShowDropdown(false)}
              options={modelOptions}
              selectedValue={version}
              onSelect={handleVersionSelect}
              position="bottom-left"
              width={230}
              showCheck={true}
              anchorRef={versionButtonRef}
            />
          </div>
        )}
      </div>

      {/* Mode Toggle or Remix Label */}
      {isRemixMode ? (
        <div className="flex justify-center items-center h-10 px-4">
          <p className="text-[18px] leading-[24px] font-medium text-white">
            Remix
          </p>
        </div>
      ) : (
        <ModeSwitch
          selectedMode={selectedMode}
          onModeChange={onModeChange}
        />
      )}

      {/* Right side - Chats toggle */}
      <div className="flex items-center gap-2 justify-end" style={{ width: '160px' }}>
        {/* Chats Toggle - Only visible in Chat mode */}
        {selectedMode === "Chat" && !isRemixMode && (
          <ToggleButton
            isActive={workspaceVisible}
            onToggle={(isActive) => onWorkspaceToggle?.()}
            text="Chats"
            rightIcon={<SidebarRightIcon />}
          />
        )}
      </div>
    </div>
  );
};

export default Header;